import React, { useCallback, useState } from "react";

import { DownMinimalArrow, Plus, Delete } from "icons";
import pluralize from "pluralize";

import metaIQApi from "apis/metaIQ";
import {
  Dropdown,
  SearchBar,
  DropdownOverlay,
  SubHeader,
  Typography,
  Checkbox,
  Button,
  PageLoader,
} from "clearsense-ui/components";
import AnimatedAction from "components/Common/AnimatedAction";
import Filter from "components/Common/FilterHeader/Filter";
import {
  FILTER_CONDITIONS,
  FILTER_OPERATIONS,
} from "components/DataManagement/constants/stewardFilter";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import ClassificationPane from "./ClassificationPane";
import { REQUEST_TABLE_COLUMN } from "./constants";
import DeleteClassification from "./DeleteClassification";
import MetaIQTable from "./Table";

const Classifications = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRequestCount, setTotalRequestCount] = useState(0);
  const [checkedRows, setCheckedRows] = useState([]);
  const [filterConditions, setFilterConditions] = useState([]);
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isAddNewClassification, setIsAddNewClassification] = useState(false);

  const fetchDatabaseDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setRequests([]);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const response = await metaIQApi.fetch(search, currentPage, pageSize);
      const auditIds = response.data.meta_iq_requests?.map(
        data => data.audit_id
      );
      setTotalRequestCount(response.data.total_pagination);
      const statusList = await Promise.all(
        auditIds.map(async id => await metaIQApi.fetchStatus(id))
      );
      const statusObjects = statusList
        .map(status => JSON.parse(status.data))
        .flat();
      response.data.meta_iq_requests.map(data => {
        const status = statusObjects.find(s => s.audit_id === data.audit_id);
        setRequests(requests => [
          ...requests,
          {
            ...data,
            status: status.job_status,
            tablesScanned: status.tables_scanned,
            comments: status.comments,
          },
        ]);
      });
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleDelete = record => {
    setRowToBeDeleted(record);
    setIsDeleteAlertOpen(true);
  };

  const { columns, toggleVisibility } = useColumnHeaderSelector(
    REQUEST_TABLE_COLUMN(handleDelete)
  );

  const filterParams = useSearchAndPaginate(fetchDatabaseDetails, {
    all: () => setRequests([]),
  });

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <SubHeader
        className="mt-8"
        breadcrumbs={[
          { label: "Reference Data", link: "#" },
          { label: "Meta IQ", link: "#" },
          { label: "Overview", link: "#" },
        ]}
        actionBlock={
          <Dropdown
            label="Create Classification"
            icon={Plus}
            data-cy="cde-add-new-button"
            primaryAction={() => {
              setIsAddNewClassification(true);
            }}
          />
        }
      />
      <div className="flex justify-between w-full mt-4">
        <div className="flex items-center gap-4 flex-nowrap">
          <Typography style="large" type="bold">
            {pluralize("Classification", totalRequestCount, true)}
          </Typography>
          <AnimatedAction>
            {checkedRows.length > 1 && (
              <Button
                style="secondary"
                icon={Delete}
                label="Delete"
                className="font-semibold text-danger"
                onClick={() => setIsDeleteAlertOpen(true)}
              />
            )}
          </AnimatedAction>
          <Filter
            title="Classifications"
            filterConditions={filterConditions}
            setFilterConditions={setFilterConditions}
            columnOptions={REQUEST_TABLE_COLUMN(handleDelete)
              ?.filter(column => column.title !== "Action")
              .map(column => ({
                label: column.title,
                value: column.title,
              }))}
            conditionOptions={FILTER_CONDITIONS}
            filterOperations={FILTER_OPERATIONS}
          />
        </div>
        <div className="flex items-center w-3/6 space-x-4">
          <SearchBar
            placeholder="Search Classification"
            className="flex justify-end w-10"
            value={filterParams.search}
            onChange={e => filterParams.setSearch(e.target.value)}
          />
          <div className="flex items-center gap-2 mt-0">
            <div className="flex gap-4">
              <DropdownOverlay
                buttonStyle="secondary"
                buttonLabel="Edit Columns"
                buttonProps={{
                  icon: DownMinimalArrow,
                  iconPosition: "right",
                  disabled: !totalRequestCount,
                }}
              >
                <div className="p-4 pb-0">
                  <Typography
                    style="h6"
                    className="mb-2 uppercase text-grey-medium-shade"
                  >
                    enabled
                  </Typography>
                  {columns?.selected?.map(column => (
                    <Checkbox
                      key={column.key}
                      label={column.title}
                      id={column.key}
                      className="mb-5"
                      onClick={e =>
                        columns.selected.length > 1 &&
                        toggleVisibility(e.target.id)
                      }
                      checked
                      readOnly
                      disabled={columns.selected.length === 1}
                    />
                  ))}
                </div>
                <div className="px-4">
                  <Typography
                    style="h6"
                    className="mb-2 uppercase text-grey-medium-shade"
                  >
                    disabled
                  </Typography>
                  {columns?.unSelected?.map(column => (
                    <Checkbox
                      key={column.key}
                      label={column.title}
                      id={column.key}
                      className="mb-5"
                      onClick={e => toggleVisibility(e.target.id)}
                      readOnly
                    />
                  ))}
                </div>
              </DropdownOverlay>
            </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <PageLoader />
      ) : (
        <MetaIQTable
          totalRequestCount={totalRequestCount}
          setIsAddNewClassification={setIsAddNewClassification}
          requests={requests}
          columns={columns}
          setCheckedRows={setCheckedRows}
          checkedRows={checkedRows}
          filterParams={filterParams}
        />
      )}
      <ClassificationPane
        fetchDatabaseDetails={fetchDatabaseDetails}
        isAddNewClassification={isAddNewClassification}
        setIsAddNewClassification={setIsAddNewClassification}
      />

      <DeleteClassification
        fetchDatabaseDetails={fetchDatabaseDetails}
        isDeleteAlertOpen={isDeleteAlertOpen}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        rowToBeDeleted={rowToBeDeleted}
        setRowToBeDeleted={setRowToBeDeleted}
        checkedRows={checkedRows}
        setCheckedRows={setCheckedRows}
      />
    </div>
  );
};
export default Classifications;
